import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import {
  CodingStrategyEnum,
  OrientationEnum,
  PaperFormatEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useEditableTrackableData } from '../../../hooks/useEditableTrackableData.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { SectionTemplatePage_OneSectionTemplate } from '../../../pages/SectionTemplate/hooks/useFindOneSectionTemplateQuery.hook';

export const useInitialValuesFactory = (
  pInitialValues?: SectionTemplatePage_OneSectionTemplate,
) => {
  const { generateId } = useIdFactory();

  const { trackableData } = useEditableTrackableData();

  const referencesContext = useReferencesContext();

  const { defaultGenericStatus, defaultSectionType } = ensureDefined({
    defaultGenericStatus: referencesContext.defaultGenericStatus,
    defaultSectionType: referencesContext.defaultSectionType,
  });

  const { codingStrategiesCodeMap, contentOriginsCodeMap, printStylesCodeMap } =
    referencesContext;

  const initialValues: SectionTemplatePage_OneSectionTemplate = useMemo(
    () => ({
      __typename: 'SectionTemplate' as const,
      _id: generateId(),
      code: EMPTY_STRING,
      label: EMPTY_STRING,
      typeId: defaultSectionType._id,
      statusId: defaultGenericStatus._id,
      customLayouts: EMPTY_ARRAY,
      ...trackableData,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      virtualRoot: {
        __typename: 'Section' as const,
        _id: generateId(),
        isVirtual: true,
        contentOriginId: contentOriginsCodeMap.Special._id,
        subSections: EMPTY_ARRAY,
        orientation: OrientationEnum.Portrait,
        paperFormat: PaperFormatEnum.A4,
        hasPagebreakAfter: false,
        hasPagebreakBefore: false,
        ...omitBy(pInitialValues?.virtualRoot, isNil),
      },
      printing: {
        __typename: 'SectionTemplatePrinting' as const,
        _id: generateId(),
        pdfPrints: EMPTY_ARRAY,
        pdfPrintsIncrement: 0,
        printSettings: {
          __typename: 'PrintSettings' as const,
          _id: generateId(),
          addFooters: true,
          addHeaders: true,
          printStyleId: printStylesCodeMap.Blank._id,
        },
      },
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      contentOriginsCodeMap.Special._id,
      defaultGenericStatus._id,
      defaultSectionType._id,
      generateId,
      pInitialValues,
      printStylesCodeMap.Blank._id,
      trackableData,
    ],
  );

  return { initialValues };
};
